import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { Leaf, ArrowLeft, Search, ChevronRight, Menu, X } from "lucide-react";
import { useI18n } from "../dashboard/i18n";
import LangToggle from "../dashboard/components/LangToggle";

/**
 * Docs — Public user manual page.
 * SEO-friendly, bilingual (TR/EN), sidebar navigation, anchor scrolling.
 */

const SECTIONS = [
  { id: "getting-started", tr: "Hesap Oluşturma", en: "Getting Started" },
  { id: "entity", tr: "Kuruluş Bilgileri", en: "Entity Setup" },
  { id: "facilities", tr: "Tesis Ekleme", en: "Add Facilities" },
  { id: "activities", tr: "Emisyon Aktivitesi", en: "Emission Activities" },
  { id: "cbam", tr: "CBAM Modülü", en: "CBAM Module" },
  { id: "standards", tr: "Standart Modülleri", en: "Standards Modules" },
  { id: "reports", tr: "Rapor Üretme", en: "Generate Reports" },
  { id: "consultant", tr: "Danışman Modu", en: "Consultant Mode" },
  { id: "settings", tr: "Ayarlar", en: "Settings" },
  { id: "faq", tr: "SSS ve Yaygın Hatalar", en: "FAQ & Common Mistakes" },
  { id: "support", tr: "Destek", en: "Support" },
];

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 border-t border-[#eef0e7] pt-10 first:border-t-0 first:pt-0" data-testid={`docs-section-${id}`}>
      <h2 className="font-display text-2xl leading-tight text-[#14512f] sm:text-3xl">{title}</h2>
      <div className="mt-4 space-y-4 text-[15px] leading-relaxed text-[#3c4239]">{children}</div>
    </section>
  );
}

const P = (p) => <p {...p} className="text-[15px] leading-relaxed text-[#3c4239]" />;
const OL = (p) => <ol {...p} className="ml-6 list-decimal space-y-2 text-[15px] leading-relaxed text-[#3c4239]" />;
const UL = (p) => <ul {...p} className="ml-6 list-disc space-y-2 text-[15px] leading-relaxed text-[#3c4239]" />;
const LI = (p) => <li {...p} />;
const Strong = (p) => <strong {...p} className="font-semibold text-[#16321f]" />;
const Code = (p) => <code {...p} className="rounded bg-[#eef0e7] px-1.5 py-0.5 font-mono text-[13px] text-[#14512f]" />;
const Note = ({ children }) => (
  <div className="rounded-xl border-l-4 border-[#14512f] bg-[#f2f5ea] p-4 text-[14px] text-[#2b3327]">
    {children}
  </div>
);

function ContentTR() {
  return (
    <>
      <Section id="getting-started" title="1. Hesap Oluşturma">
        <OL>
          <LI><Strong>https://carbon.enasuspro.com</Strong> adresine gidin.</LI>
          <LI>Hangi profilde olduğunuzu seçin — 4 kart görürsünüz:
            <UL>
              <LI><Strong>İhracatçı KOBİ</Strong> — CBAM'e tabi, tek ya da az tesisli üretici</LI>
              <LI><Strong>Kurumsal / Holding</Strong> — Çok iştirakli grup, CSRD/ESRS raporlaması</LI>
              <LI><Strong>Sürdürülebilirlik Danışmanı</Strong> — Birden çok müşteri portföyü</LI>
              <LI><Strong>Doğrulayıcı / Denetçi</Strong> — ISO 14064-3 / ISAE 3000</LI>
            </UL>
          </LI>
          <LI>Kart tıklayınca kayıt formu açılır; rol otomatik seçili gelir.</LI>
          <LI>Bilgileri doldurun: Ad Soyad · Kuruluş adı · E-posta · Güçlü bir şifre.</LI>
          <LI><Strong>Hesap oluştur</Strong> → Panel açılır.</LI>
        </OL>
        <Note>İlk kayıttan sonra 30 dakikalık ücretsiz kişisel onboarding hakkınız var. <Link to="/pricing" className="font-medium text-[#14512f] underline">Bize ulaşın</Link>.</Note>
      </Section>

      <Section id="entity" title="2. Kuruluş Bilgileri">
        <P>Panele giriş yaptıktan sonra, sol menüden <Strong>Kuruluş</Strong> sekmesine gidin.</P>
        <OL>
          <LI>Şirket adı, sektör (NACE kodu), çalışan sayısı, yıllık ciro, adres bilgilerini girin.</LI>
          <LI><Strong>Raporlama yılı</Strong> — hangi yılın verilerini raporluyorsunuz (ör. 2025).</LI>
          <LI><Strong>Baz yılı</Strong> — hedef karşılaştırmaları için referans yıl (ör. 2019). Değiştirmemesi önerilir.</LI>
          <LI><Strong>Kaydet</Strong>.</LI>
        </OL>
        <P>Bu veriler, emisyon yoğunluğu (Scope 1+2 / çalışan, ciro, tesis alanı) hesaplarında kullanılır.</P>
      </Section>

      <Section id="facilities" title="3. Tesis Ekleme">
        <OL>
          <LI>Sol menü → <Strong>Tesisler</Strong></LI>
          <LI><Strong>+ Yeni Tesis</Strong> butonuna basın</LI>
          <LI>Ad, adres, alan (m²), tesis tipi (üretim/depo/ofis) bilgilerini girin</LI>
          <LI><Strong>Ekle</Strong></LI>
        </OL>
        <P>Birden çok tesisiniz varsa hepsini ayrı ekleyin. Her tesisin emisyonu ayrı hesaplanır ve raporlarda tesis bazında ayrıştırılabilir.</P>
      </Section>

      <Section id="activities" title="4. Emisyon Aktivitesi (Kapsam 1-2-3)">
        <P>Bu, karbon ayak izinizin çekirdek işlemidir. Sol menü → <Strong>Aktiviteler</Strong>.</P>
        <OL>
          <LI><Strong>+ Yeni Aktivite</Strong> butonuna basın.</LI>
          <LI><Strong>Kapsam</Strong> seçin:
            <UL>
              <LI><Strong>Kapsam 1</Strong> — Doğrudan emisyon (kendi yaktığınız yakıt, kendi taşıtlarınız, kaçak gazlar).</LI>
              <LI><Strong>Kapsam 2</Strong> — Dolaylı emisyon (satın alınan elektrik, ısı, buhar, soğutma).</LI>
              <LI><Strong>Kapsam 3</Strong> — Değer zinciri emisyonu (satın alınan ürünler/hizmetler, iş seyahatleri, çalışan seyahati, atıklar, dış lojistik, kiralanan varlıklar, franchise, yatırımlar vb.).</LI>
            </UL>
          </LI>
          <LI><Strong>Faktör kategorisi</Strong> seçin — 500+ emisyon faktörü mevcut (örn. "Doğalgaz - m³", "Elektrik - Türkiye şebekesi 2024", "Diesel B7", "Kağıt üretimi - kg" vb.).</LI>
          <LI><Strong>Miktar</Strong> ve <Strong>birim</Strong> (kWh, kg, km, ton, m³, litre) girin.</LI>
          <LI><Strong>Tesis</Strong> ve <Strong>dönem</Strong> (yıl-çeyrek) seçin.</LI>
          <LI><Strong>Kaydet</Strong>.</LI>
        </OL>
        <P>Sistem otomatik olarak <Code>tCO₂e</Code> (ton karbondioksit eşdeğeri) değerini hesaplar ve tesis + dönem bazında ledger'a kaydeder.</P>
      </Section>

      <Section id="cbam" title="5. CBAM Modülü — İhracatçılar İçin">
        <P>AB'nin Karbon Sınır Ayarlama Mekanizması, 2026'dan itibaren tam uygulamaya geçiyor. Aşağıdaki 6 sektör kapsamda:</P>
        <UL>
          <LI>Demir-çelik</LI>
          <LI>Çimento</LI>
          <LI>Alüminyum</LI>
          <LI>Gübre</LI>
          <LI>Hidrojen</LI>
          <LI>Elektrik</LI>
        </UL>
        <P>Modülü kullanmak için:</P>
        <OL>
          <LI>Sol menü → <Strong>CBAM</Strong></LI>
          <LI><Strong>+ Yeni CBAM Kaydı</Strong></LI>
          <LI><Strong>Çeyrek dönem</Strong> seçin (Q1-Q4 / yıl)</LI>
          <LI><Strong>CN kodu</Strong> (Combined Nomenclature — AB gümrük kodu) girin. Örn. <Code>72141000</Code> (bazı çelik ürünleri).</LI>
          <LI><Strong>Menşei ülke</Strong>, <Strong>miktar</Strong> (ton), <Strong>doğrudan emisyon</Strong> (tCO₂/ton ürün), <Strong>dolaylı emisyon</Strong> (tCO₂/ton).</LI>
          <LI><Strong>Kaydet</Strong>.</LI>
          <LI>Dönem sonu → <Strong>CBAM PDF Raporu</Strong> veya <Strong>CBAM XML Export</Strong>.</LI>
        </OL>
        <Note><Strong>XML dosyası</Strong> doğrudan AB CBAM Portalı'na yüklenebilir formattadır (üretim yılı, CN kodu, tesis, üretici bilgileri dahil).</Note>
      </Section>

      <Section id="standards" title="6. Standart Modülleri">
        <P>Sol menüden aşağıdaki modüllere erişin. Her modülde ilgili verileri doldurup rapor üretebilirsiniz:</P>
        <UL>
          <LI><Strong>GRI 305</Strong> — Global Reporting Initiative emisyon açıklamaları (305-1 Scope 1, 305-2 Scope 2, 305-3 Scope 3, 305-4 intensity, 305-5 reduction).</LI>
          <LI><Strong>ESRS E1</Strong> — CSRD kapsamı iklim değişikliği açıklamaları (transition plan, targets, energy consumption, GHG emissions).</LI>
          <LI><Strong>IFRS S1</Strong> — Genel sürdürülebilirlik açıklamaları (governance, strategy, risk management, metrics & targets).</LI>
          <LI><Strong>IFRS S2</Strong> — İklim finansal açıklamaları (physical & transition risks, financed emissions, scenario analysis).</LI>
          <LI><Strong>Hedefler</Strong> — Kısa/orta/uzun vadeli net-sıfır ve kısaltma hedefleri, ilerleme takibi.</LI>
          <LI><Strong>Yoğunluk</Strong> — Çalışan başına, ciro başına, m² başına emisyon metriği.</LI>
          <LI><Strong>Karbon Vergisi</Strong> — TR ve global karbon fiyat senaryoları (Shadow price, EU-ETS, CBAM benchmark).</LI>
          <LI><Strong>Doğrulama</Strong> — ISO 14064-3 / ISAE 3000 iş akışı (finding, evidence, sign-off).</LI>
        </UL>
      </Section>

      <Section id="reports" title="7. Rapor Üretme">
        <OL>
          <LI>Sol menü → <Strong>Raporlar</Strong></LI>
          <LI><Strong>+ Yeni Rapor</Strong></LI>
          <LI>Rapor türü seçin:
            <UL>
              <LI>ISO 14064-1 (kuruluş envanteri)</LI>
              <LI>CBAM (çeyrek dönem)</LI>
              <LI>GRI 305 (yıllık)</LI>
              <LI>ESRS E1 (yıllık)</LI>
              <LI>IFRS S2 (yıllık)</LI>
            </UL>
          </LI>
          <LI>Dönem seç → <Strong>Oluştur</Strong> → PDF olarak indirilir.</LI>
          <LI>İsterseniz <Strong>Paylaş linki</Strong> üretin — müşteri/denetçiye token'lı public URL gönderin (belirli süre geçerli, iptal edilebilir).</LI>
        </OL>
      </Section>

      <Section id="consultant" title="8. Danışman Modu">
        <P>Sürdürülebilirlik danışmanı iseniz:</P>
        <OL>
          <LI>Sol menü → <Strong>Müşteriler</Strong></LI>
          <LI><Strong>+ Yeni Müşteri Ekle</Strong> — müşteri workspace'i oluşturulur.</LI>
          <LI>Her müşteri için ayrı Kuruluş, tesisler, aktiviteler, raporlar.</LI>
          <LI>Üst menüdeki <Strong>Aktif Müşteri</Strong> seçicisi ile aralarında geçiş yaparsınız.</LI>
          <LI>Her müşteri için ayrı <Strong>Markalaşma</Strong> (logo, birincil renk, PDF şablonu) ayarlayabilirsiniz — beyaz etiket raporlar.</LI>
        </OL>
      </Section>

      <Section id="settings" title="9. Ayarlar">
        <P>Sağ üst köşedeki <Strong>Profil</Strong> ikonundan:</P>
        <UL>
          <LI>Şifre değiştir</LI>
          <LI>Dil seç (TR / EN)</LI>
          <LI>Marka ayarları (logo, birincil renk, PDF şablonu)</LI>
          <LI>Çıkış yap</LI>
        </UL>
      </Section>

      <Section id="faq" title="10. SSS ve Yaygın Hatalar">
        <P><Strong>❌ Faktör seçmeden miktar girmek</Strong> → Sistem kaydetmez, önce Kapsam ve faktör seçin.</P>
        <P><Strong>❌ Yanlış birim seçmek (kWh yerine MWh)</Strong> → 1000× yanlış sonuç. Birime dikkat, kuşkulanırsanız faktör açıklamasını okuyun.</P>
        <P><Strong>❌ CBAM'de kendi ürün kodunu girmek</Strong> → AB gümrüğü <Strong>CN kodu</Strong> ister (8 basamaklı). Yanlış kodla XML kabul edilmez.</P>
        <P><Strong>❌ Baz yılını boş bırakmak</Strong> → Hedef sapması hesaplanamaz. Baz yıl mutlaka doldurulmalı ve mümkünse hiç değişmemeli.</P>
        <P><Strong>❌ Consultant modunda yanlış müşteri workspace'inde işlem</Strong> → Üst menüde <Strong>Aktif Müşteri</Strong> etiketini her zaman kontrol edin.</P>
      </Section>

      <Section id="support" title="11. Destek ve İletişim">
        <P><Strong>Bug bildirimi & öneriler:</Strong> uygulama içindeki <Strong>İletişim</Strong> sekmesi veya doğrudan e-posta.</P>
        <P><Strong>Ücretsiz onboarding:</Strong> İlk kayıttan sonra bize yazın — 30 dakikalık kişisel kurulum yapıyoruz.</P>
        <P><Strong>Kurumsal SLA:</Strong> Enterprise plan sahiplerine öncelikli destek + dedicated hesap yöneticisi.</P>
        <P><Strong>Danışmanlar:</Strong> Consultant Plan'da API erişimi ve teknik entegrasyon desteği dahil.</P>
      </Section>
    </>
  );
}

function ContentEN() {
  return (
    <>
      <Section id="getting-started" title="1. Getting Started">
        <OL>
          <LI>Go to <Strong>https://carbon.enasuspro.com</Strong>.</LI>
          <LI>Pick the persona card that best describes you — 4 options:
            <UL>
              <LI><Strong>Exporting SMB</Strong> — CBAM-liable, single or few facilities</LI>
              <LI><Strong>Enterprise / Group</Strong> — Multi-entity group, CSRD/ESRS reporting</LI>
              <LI><Strong>Sustainability Consultant</Strong> — Multi-client portfolio</LI>
              <LI><Strong>Verifier / Auditor</Strong> — ISO 14064-3 / ISAE 3000</LI>
            </UL>
          </LI>
          <LI>Click a card → registration form opens with role pre-selected.</LI>
          <LI>Fill in: Full name · Organization · Email · Strong password.</LI>
          <LI><Strong>Create account</Strong> → Dashboard opens.</LI>
        </OL>
        <Note>You get a free 30-minute personal onboarding after signup. <Link to="/pricing" className="font-medium text-[#14512f] underline">Contact us</Link>.</Note>
      </Section>

      <Section id="entity" title="2. Entity Setup">
        <P>After login, go to <Strong>Entity</Strong> in the left menu.</P>
        <OL>
          <LI>Enter company name, sector (NACE code), employee count, annual revenue, address.</LI>
          <LI><Strong>Reporting year</Strong> — which year of data you're reporting (e.g. 2025).</LI>
          <LI><Strong>Base year</Strong> — the reference year for target comparisons (e.g. 2019). Should not be changed once set.</LI>
          <LI><Strong>Save</Strong>.</LI>
        </OL>
        <P>Used for intensity metrics (Scope 1+2 / employee, revenue, facility area).</P>
      </Section>

      <Section id="facilities" title="3. Add Facilities">
        <OL>
          <LI>Left menu → <Strong>Facilities</Strong></LI>
          <LI><Strong>+ New Facility</Strong></LI>
          <LI>Enter name, address, area (m²), type (production/warehouse/office)</LI>
          <LI><Strong>Add</Strong></LI>
        </OL>
        <P>If you have multiple facilities, add each one separately. Emissions are calculated per-facility.</P>
      </Section>

      <Section id="activities" title="4. Emission Activities (Scope 1-2-3)">
        <P>The core operation for your carbon footprint. Left menu → <Strong>Activities</Strong>.</P>
        <OL>
          <LI><Strong>+ New Activity</Strong>.</LI>
          <LI>Pick <Strong>Scope</Strong>:
            <UL>
              <LI><Strong>Scope 1</Strong> — Direct emissions (your own fuel combustion, vehicles, fugitive gases).</LI>
              <LI><Strong>Scope 2</Strong> — Indirect (purchased electricity, heat, steam, cooling).</LI>
              <LI><Strong>Scope 3</Strong> — Value chain (purchased goods, travel, waste, transport, etc.).</LI>
            </UL>
          </LI>
          <LI>Select <Strong>factor category</Strong> — 500+ factors available.</LI>
          <LI>Enter <Strong>quantity</Strong> and <Strong>unit</Strong>.</LI>
          <LI>Pick <Strong>facility</Strong> and <Strong>period</Strong> (year-quarter).</LI>
          <LI><Strong>Save</Strong>.</LI>
        </OL>
        <P>System auto-calculates <Code>tCO₂e</Code>.</P>
      </Section>

      <Section id="cbam" title="5. CBAM Module — For Exporters">
        <P>EU Carbon Border Adjustment Mechanism, full enforcement starts 2026. Covers 6 sectors: Iron/Steel, Cement, Aluminum, Fertilizer, Hydrogen, Electricity.</P>
        <OL>
          <LI>Left menu → <Strong>CBAM</Strong></LI>
          <LI><Strong>+ New CBAM Record</Strong></LI>
          <LI>Pick <Strong>quarter</Strong> (Q1-Q4 / year)</LI>
          <LI>Enter <Strong>CN code</Strong> (Combined Nomenclature)</LI>
          <LI><Strong>Origin country</Strong>, <Strong>quantity</Strong> (tonnes), <Strong>direct emissions</Strong> (tCO₂/tonne), <Strong>indirect emissions</Strong> (tCO₂/tonne).</LI>
          <LI><Strong>Save</Strong>.</LI>
          <LI>End of period → <Strong>CBAM PDF</Strong> or <Strong>CBAM XML Export</Strong>.</LI>
        </OL>
        <Note>The <Strong>XML file</Strong> is ready to upload directly to the EU CBAM portal.</Note>
      </Section>

      <Section id="standards" title="6. Standards Modules">
        <UL>
          <LI><Strong>GRI 305</Strong> — Global Reporting Initiative emissions disclosures.</LI>
          <LI><Strong>ESRS E1</Strong> — CSRD climate change disclosures.</LI>
          <LI><Strong>IFRS S1</Strong> — General sustainability disclosures.</LI>
          <LI><Strong>IFRS S2</Strong> — Climate-related financial disclosures.</LI>
          <LI><Strong>Targets</Strong> — Short/mid/long-term net-zero targets, progress tracking.</LI>
          <LI><Strong>Intensity</Strong> — Per employee, per revenue, per m².</LI>
          <LI><Strong>Carbon Tax</Strong> — TR and global carbon pricing scenarios.</LI>
          <LI><Strong>Verification</Strong> — ISO 14064-3 / ISAE 3000 workflow.</LI>
        </UL>
      </Section>

      <Section id="reports" title="7. Generate Reports">
        <OL>
          <LI>Left menu → <Strong>Reports</Strong></LI>
          <LI><Strong>+ New Report</Strong></LI>
          <LI>Pick type: ISO 14064-1 · CBAM · GRI 305 · ESRS E1 · IFRS S2</LI>
          <LI>Pick period → <Strong>Generate</Strong> → downloads as PDF</LI>
          <LI>Optionally <Strong>share link</Strong> — send token-based public URL.</LI>
        </OL>
      </Section>

      <Section id="consultant" title="8. Consultant Mode">
        <OL>
          <LI>Left menu → <Strong>Clients</Strong></LI>
          <LI><Strong>+ Add New Client</Strong> — client workspace created.</LI>
          <LI>Separate entity, facilities, activities, reports per client.</LI>
          <LI>Top bar <Strong>Active Client</Strong> selector to switch.</LI>
          <LI>Per-client <Strong>branding</Strong> for white-label reports.</LI>
        </OL>
      </Section>

      <Section id="settings" title="9. Settings">
        <UL>
          <LI>Change password</LI>
          <LI>Language (TR / EN)</LI>
          <LI>Branding (logo, primary color, PDF template)</LI>
          <LI>Sign out</LI>
        </UL>
      </Section>

      <Section id="faq" title="10. FAQ & Common Mistakes">
        <P><Strong>❌ Entering quantity without a factor</Strong> → won't save.</P>
        <P><Strong>❌ Wrong unit (kWh vs MWh)</Strong> → 1000× wrong. Always double-check unit.</P>
        <P><Strong>❌ Using own product code in CBAM</Strong> → must be EU CN code (8 digits).</P>
        <P><Strong>❌ Empty base year</Strong> → target deviation cannot be calculated.</P>
        <P><Strong>❌ Working in wrong client workspace</Strong> → check Active Client label at top.</P>
      </Section>

      <Section id="support" title="11. Support & Contact">
        <P><Strong>Bug reports & suggestions:</Strong> Contact tab inside app or direct email.</P>
        <P><Strong>Free onboarding:</Strong> 30-minute personal setup after signup.</P>
        <P><Strong>Enterprise SLA:</Strong> Priority support + dedicated account manager.</P>
        <P><Strong>Consultants:</Strong> API access and technical integration support included in Consultant Plan.</P>
      </Section>
    </>
  );
}

export default function Docs() {
  const { lang } = useI18n();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [active, setActive] = useState(SECTIONS[0].id);

  // Update document title for SEO
  useEffect(() => {
    document.title = lang === "en"
      ? "Docs · Enasuspro® Carbon Ledger — User Manual"
      : "Dokümanlar · Enasuspro® Carbon Ledger — Kullanıcı Kılavuzu";
  }, [lang]);

  // Track active section on scroll
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) setActive(visible[0].target.id);
      },
      { rootMargin: "-30% 0px -60% 0px" }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, [lang]);

  const navItems = useMemo(
    () => SECTIONS.map((s) => ({ id: s.id, label: lang === "en" ? s.en : s.tr })),
    [lang]
  );

  return (
    <div className="min-h-screen bg-[#FAFAF5] text-[#1b2420]">

      {/* Top bar */}
      <header className="sticky top-0 z-50 border-b border-[#eef0e7] bg-[#FAFAF5]/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <Link to="/" className="inline-flex items-center gap-2.5" data-testid="docs-home-link">
            <span className="grid h-9 w-9 place-items-center rounded-lg bg-[#14512f] text-[#D4F870]">
              <Leaf className="h-4 w-4" strokeWidth={2.5} />
            </span>
            <span className="font-display text-[15px] tracking-tight text-[#14512f] sm:text-base">
              Enasuspro<sup className="text-[9px] text-[#6b7268]">®</sup> Carbon Ledger
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="hidden text-[13px] font-medium text-[#14512f] hover:underline sm:inline" data-testid="docs-back-home">
              <ArrowLeft className="mr-1 inline h-3.5 w-3.5" /> {lang === "en" ? "Home" : "Ana sayfa"}
            </Link>
            <LangToggle />
            <button
              type="button"
              className="rounded-md border border-[#dcdfd4] p-1.5 lg:hidden"
              onClick={() => setSidebarOpen((v) => !v)}
              data-testid="docs-sidebar-toggle"
              aria-label="toggle nav">
              {sidebarOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="mx-auto flex max-w-6xl gap-10 px-4 py-10 sm:px-6 lg:py-14">

        {/* Sidebar */}
        <aside className={`${sidebarOpen ? "fixed inset-x-0 bottom-0 top-[70px] z-40 overflow-y-auto bg-[#FAFAF5] p-6" : "hidden"} lg:static lg:z-auto lg:block lg:h-fit lg:w-60 lg:shrink-0 lg:overflow-visible lg:bg-transparent lg:p-0 lg:sticky lg:top-16`} data-testid="docs-sidebar">
          <div className="mb-3 text-[10px] font-medium uppercase tracking-[0.18em] text-[#8a8f83]">
            {lang === "en" ? "Contents" : "İçindekiler"}
          </div>
          <nav className="space-y-1">
            {navItems.map((n) => (
              <a
                key={n.id}
                href={`#${n.id}`}
                onClick={() => setSidebarOpen(false)}
                data-testid={`docs-nav-${n.id}`}
                className={`block rounded-lg px-3 py-2 text-[13px] leading-snug transition-colors ${
                  active === n.id
                    ? "bg-[#14512f]/10 font-medium text-[#14512f]"
                    : "text-[#4b544c] hover:bg-[#eef0e7] hover:text-[#14512f]"
                }`}
              >
                <ChevronRight className={`mr-1 inline h-3 w-3 transition-transform ${active === n.id ? "translate-x-0.5" : ""}`} />
                {n.label}
              </a>
            ))}
          </nav>

          <div className="mt-8 rounded-xl border border-[#dcdfd4] bg-white p-4 text-[13px] text-[#4b544c]">
            <div className="font-medium text-[#14512f]">
              {lang === "en" ? "Ready to try?" : "Denemeye hazır mısınız?"}
            </div>
            <p className="mt-1 text-[12px] leading-relaxed">
              {lang === "en"
                ? "3-day free trial · personal onboarding · tailored pricing."
                : "3 gün ücretsiz deneme · kişisel onboarding · size özel teklif."}
            </p>
            <Link to="/pricing" className="mt-3 inline-flex items-center gap-1 text-[12px] font-medium text-[#14512f] hover:underline" data-testid="docs-cta-pricing">
              {lang === "en" ? "Get a quote →" : "Teklif alın →"}
            </Link>
          </div>
        </aside>

        {/* Content */}
        <main className="min-w-0 flex-1" data-testid="docs-content">
          <div className="mb-8">
            <p className="inline-flex items-center gap-2 rounded-full border border-[#14512f]/15 bg-white/70 px-3 py-1 text-[11px] font-medium uppercase tracking-widest text-[#14512f]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#14512f]" />
              {lang === "en" ? "User Manual · v1.0" : "Kullanıcı Kılavuzu · v1.0"}
            </p>
            <h1 className="mt-4 font-display text-4xl leading-[1.05] tracking-tight text-[#16321f] sm:text-5xl" data-testid="docs-title">
              {lang === "en"
                ? "Enasuspro® Carbon Ledger — Docs"
                : "Enasuspro® Carbon Ledger — Dokümanlar"}
            </h1>
            <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-[#5c6259]" data-testid="docs-subtitle">
              {lang === "en"
                ? "Complete guide to setting up your entity, adding facilities, tracking Scope 1-2-3 emissions, and generating CBAM, GRI, ESRS, IFRS S1/S2 compliant reports."
                : "Kuruluşunuzu kurma, tesisleri ekleme, Kapsam 1-2-3 emisyonlarını takip etme ve CBAM, GRI, ESRS, IFRS S1/S2 uyumlu raporlar üretme rehberi."}
            </p>
          </div>

          <div className="space-y-10">
            {lang === "en" ? <ContentEN /> : <ContentTR />}
          </div>

          {/* Footer */}
          <div className="mt-16 border-t border-[#eef0e7] pt-8 text-[13px] text-[#6b7268]">
            <p>
              {lang === "en" ? "Enasuspro® — 35+ years environmental engineering & ESG consulting experience in one platform." : "Enasuspro® — 35+ yıllık çevre mühendisliği ve ESG danışmanlık tecrübesi tek platformda."}
            </p>
            <p className="mt-2">
              {lang === "en" ? "Founder & Senior Consultant/Auditor:" : "Kurucumuz ve Kıdemli Danışman/Denetçi:"} <Strong>Barış YÜCE</Strong>
            </p>
            <p className="mt-2">
              <Link to="/pricing" className="text-[#14512f] hover:underline" data-testid="docs-footer-pricing">
                {lang === "en" ? "See plans" : "Planları gör"}
              </Link>
              {" · "}
              <Link to="/login" className="text-[#14512f] hover:underline" data-testid="docs-footer-login">
                {lang === "en" ? "Sign in" : "Giriş yap"}
              </Link>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
